import { useEffect, useMemo, useState } from "react";
import { Target, Crosshair, Brain, X, History, SlidersHorizontal, TrendingDown, TrendingUp, ChevronRight, Flame } from "lucide-react";
import { MUSCLE_CATALOG, MUSCLE_BY_ID, canonicalMuscleId } from "../../data/muscleAtlas";
import { bodyStageGeometry } from "../../data/bodyAtlasBounds";
import { api } from "../../lib/api";

/**
 * Iter AH — AnatomyViewerV2: clinician-first redesign.
 *
 * Replaces the multi-overlay encyclopedia model with a body-as-navigation,
 * 3-tab decision-support tool:
 *
 *   ANATOMY          — muscle overlays + treated-this-session + heatmap intensity
 *   PAIN PATTERNS    — referral patterns + trigger points + symptom zones
 *   CLINICAL INSIGHTS — Why it matters / Symptoms / Works with / Evaluate next /
 *                       Client history / Outcome trends — all client-specific
 *
 * Layout:
 *   • Desktop: large anatomy on the left, sticky clinical detail panel on the right.
 *   • Mobile: full-bleed anatomy; clinical detail collapses into a bottom sheet
 *     that appears when a muscle is selected — anatomy stays visible.
 *
 * Data sources:
 *   • MUSCLE_CATALOG for clinical reference (symptoms, frequentlyWorksWith, referral)
 *   • GET /api/clients/{id}/body-chart-timeline for per-client history + outcomes
 */

const TABS = [
  { id: "anatomy", label: "Anatomy", icon: Target },
  { id: "pain", label: "Pain Patterns", icon: Crosshair },
  { id: "insights", label: "Clinical Insights", icon: Brain },
];

const TRIGGER_COLOR = { common: "#C53D2E", secondary: "#D49B2E", possible: "#9C8A6E" };

// Clinical 1–7 intensity scale (mirrors a 0–10 NRS bucketing used in the panel)
//   1–2 → green (mild)     3–4 → yellow (moderate)
//   5   → orange (high)    6–7 → red (severe / primary)
function intensityScoreColor(score) {
  if (score >= 6) return "#C53D2E";   // red — severe / primary contributor
  if (score >= 5) return "#E2842F";   // orange — high
  if (score >= 3) return "#E6C03A";   // yellow — moderate
  return "#4CA771";                   // green — mild / possible
}

// Map heatmap intensity (0..1) to a 1–7 clinical score
function heatToScore(intensity) {
  const v = Math.max(0, Math.min(1, intensity || 0));
  return Math.max(1, Math.round(v * 7));
}

// Map trigger-point atlas "type" to a default intensity score so unified marker
// styling can be applied even when no per-client heatmap data is present.
function triggerTypeScore(type) {
  if (type === "common") return 6;
  if (type === "secondary") return 4;
  return 2; // possible / unknown
}

function intensityColor(intensity) {
  return intensityScoreColor(heatToScore(intensity));
}

function avgPainDelta(deltas) {
  if (!deltas || !deltas.length) return null;
  const sum = deltas.reduce((a, b) => a + (b || 0), 0);
  return Math.round((sum / deltas.length) * 10) / 10;
}

function avgRomGain(gains) {
  if (!gains || !gains.length) return null;
  const sum = gains.reduce((a, b) => a + (b || 0), 0);
  return Math.round(sum / gains.length);
}

export default function AnatomyViewerV2({
  clientId,
  sessionId,                                   // reserved for future per-session caching
  currentSessionWorked = [],                   // muscles tapped in current session
  previousSessionWorked = [],                  // muscles from the previous session
  selectedMuscleId = null,
  onMuscleSelect = () => {},
  onAddToWorked = null,                        // optional: shows "Add to today's work" button
  onRemoveFromWorked = null,                   // optional: shows "Remove" button when already worked today
  sex = "m",
  defaultView = "anterior",
  testIdPrefix = "anatomy-v2",
  patientName = null,                          // optional — header label
  patientMeta = null,                          // optional — second line (e.g., "Session 6 of 12")
  hideTopBar = false,                          // when DiagramSection wraps its own chrome
}) {
  const [view, setView] = useState(defaultView);
  const [activeTab, setActiveTab] = useState("anatomy");
  const [layer, setLayer] = useState("muscles"); // muscles | bones | landmarks
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Fetch per-client timeline (heatmap + per-muscle outcomes + recurring) once per clientId.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setTimelineLoading(true);
    api.get(`/clients/${clientId}/body-chart-timeline`)
      .then((r) => { if (!cancelled) setTimeline(r.data); })
      .catch(() => { if (!cancelled) setTimeline(null); })
      .finally(() => { if (!cancelled) setTimelineLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  const heatmap = timeline?.heatmap || {};
  const muscleRows = timeline?.muscles || [];
  const recurring = useMemo(() => new Set(timeline?.recurring_muscles || []), [timeline]);

  const currentSet = useMemo(() => new Set(currentSessionWorked), [currentSessionWorked]);
  const prevSet = useMemo(() => new Set(previousSessionWorked), [previousSessionWorked]);

  // Active muscle = clicked muscle, full clinical record from atlas
  const activeMuscle = selectedMuscleId
    ? (MUSCLE_BY_ID[selectedMuscleId] || MUSCLE_BY_ID[canonicalMuscleId(selectedMuscleId)])
    : null;
  const activeHeat = activeMuscle ? (heatmap[activeMuscle.id] || heatmap[canonicalMuscleId(activeMuscle.id)]) : null;
  const activeRow = activeMuscle ? muscleRows.find((m) => m.muscle_id === activeMuscle.id) : null;

  const visibleMuscles = useMemo(() => MUSCLE_CATALOG.filter((m) => (m.visibleOn || []).includes(view)), [view]);

  return (
    <div className="bg-paper-50 rounded-3xl overflow-hidden border border-paper-200" data-testid={testIdPrefix}>
      {/* ─── TOP BAR ────────────────────────────────────────────────────────── */}
      {!hideTopBar && (
        <div className="bg-ink-900 text-white px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`${testIdPrefix}-topbar`}>
          <div className="min-w-0">
            {patientName ? (
              <>
                <div className="font-medium text-base text-white truncate">{patientName}</div>
                {patientMeta && <div className="text-xs text-white/70 truncate">{patientMeta}</div>}
              </>
            ) : (
              <div className="text-sm text-white/80">Anatomy viewer</div>
            )}
          </div>
          <TabRibbon activeTab={activeTab} setActiveTab={setActiveTab} testIdPrefix={testIdPrefix} />
          <div className="flex items-center gap-2 text-white/70">
            <button type="button" className="inline-flex items-center gap-1.5 text-xs hover:text-white" data-testid={`${testIdPrefix}-history`}>
              <History className="w-3.5 h-3.5" /> History
            </button>
            <button type="button" className="inline-flex items-center gap-1.5 text-xs hover:text-white" data-testid={`${testIdPrefix}-filters`}>
              <SlidersHorizontal className="w-3.5 h-3.5" /> Filters
            </button>
          </div>
        </div>
      )}
      {hideTopBar && (
        // Compact tab strip when the parent renders its own header
        <div className="bg-ink-900 text-white px-4 py-2 flex items-center justify-center" data-testid={`${testIdPrefix}-tabstrip`}>
          <TabRibbon activeTab={activeTab} setActiveTab={setActiveTab} testIdPrefix={testIdPrefix} />
        </div>
      )}

      {/* ─── MAIN GRID ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-[180px,1fr,380px] gap-0">
        {/* LEFT RAIL — controls (front/back, layer, intensity legend) */}
        <aside className="hidden lg:block border-r border-paper-200 px-4 py-5 space-y-5" data-testid={`${testIdPrefix}-left-rail`}>
          <div>
            <div className="flex bg-paper-100 rounded-2xl p-1">
              <button
                type="button"
                onClick={() => setView("anterior")}
                data-testid={`${testIdPrefix}-front`}
                className={`flex-1 min-h-[34px] rounded-xl text-sm font-medium transition-colors ${view === "anterior" ? "bg-white text-ink-900 shadow-sm" : "text-ink-600 hover:text-ink-900"}`}
              >
                Front
              </button>
              <button
                type="button"
                onClick={() => setView("posterior")}
                data-testid={`${testIdPrefix}-back`}
                className={`flex-1 min-h-[34px] rounded-xl text-sm font-medium transition-colors ${view === "posterior" ? "bg-white text-ink-900 shadow-sm" : "text-ink-600 hover:text-ink-900"}`}
              >
                Back
              </button>
            </div>
          </div>

          <div>
            <div className="label-overline mb-2">Layer</div>
            <div className="space-y-1.5">
              {[
                { key: "muscles",   label: "Muscles",   dot: "bg-terracotta-500" },
                { key: "bones",     label: "Bones",     dot: "bg-paper-300" },
                { key: "landmarks", label: "Landmarks", dot: "bg-paper-300" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setLayer(opt.key)}
                  data-testid={`${testIdPrefix}-layer-${opt.key}`}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-2xl text-sm transition-colors ${layer === opt.key ? "bg-paper-100 text-ink-900 font-medium" : "text-ink-600 hover:bg-paper-100"}`}
                >
                  <span className={`w-2 h-2 rounded-full ${layer === opt.key ? opt.dot : "bg-paper-200"}`} />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="label-overline mb-2">Intensity (1–7)</div>
            <div className="space-y-1 text-xs">
              <LegendDot color="#C53D2E" label="6–7" sub="Severe" />
              <LegendDot color="#E2842F" label="5" sub="High" />
              <LegendDot color="#E6C03A" label="3–4" sub="Moderate" />
              <LegendDot color="#4CA771" label="1–2" sub="Mild" />
              <LegendDot ring="#7A8F70" label="Treated Today" />
              <LegendDot ring="#3B6EAE" label="Previously" />
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowHeatmap((v) => !v)}
            data-testid={`${testIdPrefix}-heatmap-toggle`}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-2xl border border-paper-200 text-sm hover:bg-paper-100"
          >
            <span className="inline-flex items-center gap-2">
              <Flame className="w-3.5 h-3.5 text-amber-500" />
              Heatmap
            </span>
            <span className="text-xs text-ink-500">{showHeatmap ? "On" : "Off"}</span>
          </button>
        </aside>

        {/* CENTER — ANATOMY STAGE (hero) */}
        <div
          className="relative px-0 sm:px-4 py-3 sm:py-5 flex items-center justify-center"
          style={{
            background: "radial-gradient(ellipse at 50% 30%, #FBF8F1 0%, #F4EFE3 60%, #ECE5D2 100%)",
          }}
          data-testid={`${testIdPrefix}-stage-wrap`}
        >
          {/* Mobile-only floating controls overlaid on the stage */}
          <div className="lg:hidden absolute top-3 left-3 right-3 z-10 flex items-center justify-between pointer-events-none">
            <div className="flex bg-white/85 backdrop-blur-md rounded-full p-1 shadow-sm border border-paper-200 pointer-events-auto">
              <button
                type="button"
                onClick={() => setView("anterior")}
                data-testid={`${testIdPrefix}-m-front`}
                className={`min-h-[32px] px-3.5 rounded-full text-xs font-medium transition-colors ${view === "anterior" ? "bg-ink-900 text-white" : "text-ink-700"}`}
              >Front</button>
              <button
                type="button"
                onClick={() => setView("posterior")}
                data-testid={`${testIdPrefix}-m-back`}
                className={`min-h-[32px] px-3.5 rounded-full text-xs font-medium transition-colors ${view === "posterior" ? "bg-ink-900 text-white" : "text-ink-700"}`}
              >Back</button>
            </div>
            <button
              type="button"
              onClick={() => setShowHeatmap((v) => !v)}
              data-testid={`${testIdPrefix}-m-heatmap`}
              className="pointer-events-auto bg-white/85 backdrop-blur-md text-xs px-3 min-h-[32px] rounded-full border border-paper-200 inline-flex items-center gap-1.5 shadow-sm"
            >
              <Flame className={`w-3 h-3 ${showHeatmap ? "text-amber-500" : "text-ink-400"}`} />
              <span className="text-ink-700">{showHeatmap ? "On" : "Off"}</span>
            </button>
          </div>

          <AnatomyStage
            view={view}
            sex={sex}
            visibleMuscles={visibleMuscles}
            heatmap={heatmap}
            showHeatmap={showHeatmap && activeTab === "anatomy"}
            showTriggers={activeTab === "pain"}
            showReferral={activeTab === "pain" && !!activeMuscle}
            currentSet={currentSet}
            prevSet={prevSet}
            recurring={recurring}
            selectedMuscleId={selectedMuscleId}
            onMuscleSelect={onMuscleSelect}
            activeMuscle={activeMuscle}
            testIdPrefix={testIdPrefix}
          />
          {timelineLoading && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[11px] text-ink-500 bg-white/80 backdrop-blur-md px-3 py-1 rounded-full" data-testid={`${testIdPrefix}-loading`}>Loading history…</div>
          )}
        </div>

        {/* RIGHT PANEL — clinical detail (desktop sticky); becomes bottom sheet on mobile */}
        <ClinicalDetailPanel
          activeMuscle={activeMuscle}
          activeHeat={activeHeat}
          activeRow={activeRow}
          activeTab={activeTab}
          recurring={recurring}
          currentSet={currentSet}
          onAddToWorked={onAddToWorked}
          onRemoveFromWorked={onRemoveFromWorked}
          onClose={() => onMuscleSelect(null)}
          onMuscleSelect={onMuscleSelect}
          testIdPrefix={testIdPrefix}
        />
      </div>
    </div>
  );
}

// ─── TAB RIBBON ──────────────────────────────────────────────────────────────
function TabRibbon({ activeTab, setActiveTab, testIdPrefix }) {
  return (
    <div className="inline-flex bg-white/10 rounded-full p-1" role="tablist" data-testid={`${testIdPrefix}-tabs`}>
      {TABS.map((t) => {
        const Icon = t.icon;
        const on = activeTab === t.id;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => setActiveTab(t.id)}
            data-testid={`${testIdPrefix}-tab-${t.id}`}
            className={`min-h-[36px] px-4 sm:px-5 rounded-full text-sm font-medium inline-flex items-center gap-1.5 transition-all ${on ? "bg-white text-ink-900" : "text-white/80 hover:text-white"}`}
          >
            <Icon className="w-3.5 h-3.5" />
            <span>{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── LEGEND DOT ──────────────────────────────────────────────────────────────
function LegendDot({ color, ring, label, sub }) {
  return (
    <div className="flex items-center gap-2 text-ink-700">
      {color ? (
        <span className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
      ) : (
        <span className="w-3 h-3 rounded-full shrink-0 border-2" style={{ borderColor: ring }} />
      )}
      <span>{label}</span>
      {sub && <span className="text-ink-500">{sub}</span>}
    </div>
  );
}

// ─── ANATOMY STAGE ───────────────────────────────────────────────────────────
function AnatomyStage({
  view, sex, visibleMuscles, heatmap,
  showHeatmap, showTriggers, showReferral,
  currentSet, prevSet, recurring,
  selectedMuscleId, onMuscleSelect, activeMuscle, testIdPrefix,
}) {
  const figureSrc = `/anatomy/atlas/${view}_${sex || "m"}.png`;
  const geom = useMemo(() => bodyStageGeometry(view, sex || "m"), [view, sex]);

  return (
    <div
      className="relative w-full"
      style={{
        // Hero sizing: dominant body, mobile-first 60-65 vh, capped on desktop.
        maxHeight: "min(70vh, 760px)",
        height: "min(65vh, 720px)",
      }}
      data-testid={`${testIdPrefix}-stage-wrap-inner`}
    >
      <div
        className="relative mx-auto h-full select-none"
        style={{ aspectRatio: geom.aspect }}
        data-testid={`${testIdPrefix}-stage`}
        data-view={view}
      >
        {/* Soft halo backdrop behind the body for that premium-medical feel */}
        <div
          aria-hidden
          className="absolute -inset-4 sm:-inset-6 rounded-[40px] pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at 50% 35%, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0) 70%)",
            filter: "blur(12px)",
          }}
        />
        <div className="relative w-full h-full overflow-hidden rounded-3xl">
          <img
            src={figureSrc}
            alt={`Anatomical ${view} view`}
            style={geom.imgStyle}
            draggable={false}
          />
          <svg viewBox="0 0 100 200" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
            <defs>
              <radialGradient id={`${testIdPrefix}-sel-glow`} cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#1A1A1A" stopOpacity="0.20" />
                <stop offset="55%" stopColor="#1A1A1A" stopOpacity="0.08" />
                <stop offset="100%" stopColor="#1A1A1A" stopOpacity="0" />
              </radialGradient>
              <filter id={`${testIdPrefix}-soft`} x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="0.45" />
              </filter>
              {/* Soft drop shadow used to lift clinical markers off any background layer */}
              <filter id={`${testIdPrefix}-marker-shadow`} x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="0.45" stdDeviation="0.55" floodColor="#0B0B0B" floodOpacity="0.35" />
              </filter>
            </defs>

            {/* Heatmap intensity markers — high-contrast clinical style */}
            {showHeatmap && visibleMuscles.map((m) => {
              const h = heatmap[m.id] || heatmap[canonicalMuscleId(m.id)];
              if (!h || !h.count) return null;
              const pt = (m.triggerPoints || []).find((p) => p.position?.view === view);
              if (!pt) return null;
              const cx = pt.position.x;
              const cy = (pt.position.y || 0) * 2;
              const score = heatToScore(h.intensity);
              const isActive = activeMuscle && (activeMuscle.id === m.id);
              return (
                <ClinicalMarker
                  key={`heat-${m.id}`}
                  cx={cx}
                  cy={cy}
                  score={score}
                  selected={isActive}
                  recurring={recurring.has(m.id)}
                  filterPrefix={testIdPrefix}
                  testId={`${testIdPrefix}-heat-${m.id}`}
                />
              );
            })}

            {/* Trigger point markers — Pain Patterns tab */}
            {showTriggers && visibleMuscles.flatMap((m) =>
              (m.triggerPoints || []).filter((p) => p.position?.view === view).map((tp, i) => {
                const score = triggerTypeScore(tp.type);
                const isActive = activeMuscle && (activeMuscle.id === m.id);
                return (
                  <ClinicalMarker
                    key={`tp-${m.id}-${i}`}
                    cx={tp.position.x}
                    cy={(tp.position.y || 0) * 2}
                    score={score}
                    selected={isActive}
                    filterPrefix={testIdPrefix}
                    testId={`${testIdPrefix}-tp-${m.id}-${i}`}
                  />
                );
              })
            )}

            {/* Referral halo — Pain Patterns tab, active muscle only */}
            {showReferral && activeMuscle && (activeMuscle.triggerPoints || [])
              .filter((p) => p.position?.view === view)
              .map((tp, i) => (
                <circle
                  key={`ref-${i}`}
                  cx={tp.position.x}
                  cy={(tp.position.y || 0) * 2}
                  r={9}
                  fill={TRIGGER_COLOR[tp.type] || TRIGGER_COLOR.common}
                  opacity={0.14}
                  data-testid={`${testIdPrefix}-ref-${activeMuscle.id}-${i}`}
                />
              ))}

            {/* Treated-this-session + previously-treated rings */}
            {visibleMuscles.map((m) => {
              const isCurrent = currentSet.has(m.id) || currentSet.has(canonicalMuscleId(m.id));
              const isPrev = !isCurrent && (prevSet.has(m.id) || prevSet.has(canonicalMuscleId(m.id)));
              if (!isCurrent && !isPrev) return null;
              const pt = (m.triggerPoints || []).find((p) => p.position?.view === view);
              if (!pt) return null;
              const cx = pt.position.x;
              const cy = (pt.position.y || 0) * 2;
              const stroke = isCurrent ? "#7A8F70" : "#3B6EAE";
              return (
                <g key={`ring-${m.id}`}>
                  <circle
                    cx={cx} cy={cy} r={5.4}
                    fill="none" stroke={stroke} strokeWidth={0.7} opacity={0.9}
                    data-testid={`${testIdPrefix}-${isCurrent ? "current" : "prev"}-${m.id}`}
                  />
                  {isCurrent && (
                    <circle cx={cx} cy={cy} r={6.6} fill="none" stroke={stroke} strokeWidth={0.35} opacity={0.45} />
                  )}
                </g>
              );
            })}

            {/* Selected muscle anchor glow — soft halo behind whichever marker is active.
                Individual markers (heat / trigger) draw their own selected ring/pulse. */}
            {activeMuscle && (() => {
              const pt = (activeMuscle.triggerPoints || []).find((p) => p.position?.view === view);
              if (!pt) return null;
              const cx = pt.position.x;
              const cy = (pt.position.y || 0) * 2;
              return (
                <g data-testid={`${testIdPrefix}-selected-anchor`}>
                  <circle cx={cx} cy={cy} r={11} fill={`url(#${testIdPrefix}-sel-glow)`} />
                </g>
              );
            })()}

            {/* Standalone selected marker — guarantees the active muscle stays
                visible on tabs where heatmap & trigger markers aren't drawn
                (e.g. Clinical Insights). Skipped when an underlying marker
                already renders the selection. */}
            {activeMuscle && !showHeatmap && !showTriggers && (() => {
              const pt = (activeMuscle.triggerPoints || []).find((p) => p.position?.view === view);
              if (!pt) return null;
              const cx = pt.position.x;
              const cy = (pt.position.y || 0) * 2;
              // Prefer per-client heat score; fall back to atlas trigger type
              const h = heatmap[activeMuscle.id] || heatmap[canonicalMuscleId(activeMuscle.id)];
              const score = h && h.intensity != null
                ? heatToScore(h.intensity)
                : triggerTypeScore(pt.type);
              return (
                <ClinicalMarker
                  cx={cx}
                  cy={cy}
                  score={score}
                  selected
                  recurring={recurring.has(activeMuscle.id)}
                  filterPrefix={testIdPrefix}
                  testId={`${testIdPrefix}-selected-marker`}
                />
              );
            })()}

            {/* Tappable muscle hotspots — generous tap targets */}
            {visibleMuscles.map((m) => {
              const pt = (m.triggerPoints || []).find((p) => p.position?.view === view);
              if (!pt) return null;
              const cx = pt.position.x;
              const cy = (pt.position.y || 0) * 2;
              return (
                <circle
                  key={`hit-${m.id}`}
                  cx={cx} cy={cy} r={7}
                  fill="transparent"
                  style={{ cursor: "pointer" }}
                  onClick={(e) => { e.stopPropagation(); onMuscleSelect(m.id); }}
                  data-testid={`${testIdPrefix}-tap-${m.id}`}
                  data-muscle-id={m.id}
                >
                  <title>{m.label}</title>
                </circle>
              );
            })}
          </svg>

          {/* Selected muscle tooltip pill — anchored to the body */}
          {activeMuscle && (() => {
            const pt = (activeMuscle.triggerPoints || []).find((p) => p.position?.view === view);
            if (!pt) return null;
            // Keep label clear of the top edge by flipping below when near the top
            const top = pt.position.y;
            const flipBelow = top < 12;
            return (
              <div
                className="absolute pointer-events-none"
                style={{
                  left: `${pt.position.x}%`,
                  top: `${top}%`,
                  transform: flipBelow ? "translate(-50%, 60%)" : "translate(-50%, -130%)",
                }}
                data-testid={`${testIdPrefix}-tooltip`}
              >
                <div className="px-2.5 py-1 rounded-full bg-ink-900/95 text-white text-[11px] font-medium shadow-lg whitespace-nowrap backdrop-blur-md">
                  {activeMuscle.label}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ─── CLINICAL DETAIL PANEL ───────────────────────────────────────────────────
function ClinicalDetailPanel({ activeMuscle, activeHeat, activeRow, activeTab, recurring, currentSet, onAddToWorked, onRemoveFromWorked, onClose, onMuscleSelect, testIdPrefix }) {
  // Iter AH polish — only render ONE variant at a time so data-testids stay unique
  // (Playwright strict-mode locators previously matched both desktop + mobile copies).
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 1024px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange);
    };
  }, []);

  if (!activeMuscle) {
    return isDesktop ? (
      <aside className="border-l border-paper-200 bg-white px-6 py-8" data-testid={`${testIdPrefix}-detail-empty`}>
        <div className="text-ink-500 text-sm">
          <Brain className="w-5 h-5 text-paper-300 mb-2" />
          <div className="font-medium text-ink-700 mb-1">Tap a muscle</div>
          <div>Why it matters, common symptoms, recommended next muscles, and this client&rsquo;s outcome trend will appear here.</div>
        </div>
      </aside>
    ) : null;
  }

  if (isDesktop) {
    return (
      <aside className="border-l border-paper-200 bg-white" data-testid={`${testIdPrefix}-detail`}>
        <ClinicalDetailContent
          activeMuscle={activeMuscle}
          activeHeat={activeHeat}
          activeRow={activeRow}
          activeTab={activeTab}
          recurring={recurring}
          currentSet={currentSet}
          onAddToWorked={onAddToWorked}
          onRemoveFromWorked={onRemoveFromWorked}
          onClose={onClose}
          onMuscleSelect={onMuscleSelect}
          testIdPrefix={testIdPrefix}
        />
      </aside>
    );
  }

  // Mobile bottom sheet
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-30 bg-white border-t border-paper-200 rounded-t-3xl shadow-2xl"
      style={{ maxHeight: "70vh" }}
      data-testid={`${testIdPrefix}-detail-sheet`}
    >
      <div className="overflow-y-auto" style={{ maxHeight: "70vh" }}>
        <ClinicalDetailContent
          activeMuscle={activeMuscle}
          activeHeat={activeHeat}
          activeRow={activeRow}
          activeTab={activeTab}
          recurring={recurring}
          currentSet={currentSet}
          onAddToWorked={onAddToWorked}
          onRemoveFromWorked={onRemoveFromWorked}
          onClose={onClose}
          onMuscleSelect={onMuscleSelect}
          testIdPrefix={testIdPrefix}
          isMobile
        />
      </div>
    </div>
  );
}

function ClinicalDetailContent({ activeMuscle, activeHeat, activeRow, activeTab, recurring, currentSet, onAddToWorked, onRemoveFromWorked, onClose, onMuscleSelect, testIdPrefix, isMobile }) {
  const isRecurring = recurring.has(activeMuscle.id);
  const isPrimary = (activeHeat?.intensity || 0) >= 0.66;
  const isWorkedToday = currentSet && (currentSet.has(activeMuscle.id) || currentSet.has(canonicalMuscleId(activeMuscle.id)));
  const symptoms = activeMuscle.clinical?.commonComplaints || [];
  const worksWith = activeMuscle.clinical?.frequentlyWorksWith || [];
  const referral = activeMuscle.referralPattern || "";

  // Evaluate-next: prefer Works-With minus already-recurring, else use postural associations
  const evaluateNext = worksWith.slice(0, 6);

  const painDelta = avgPainDelta(activeRow?.pain_deltas);
  const romGain = avgRomGain(activeRow?.rom_gains);

  return (
    <div className="p-5 sm:p-6 space-y-5" data-testid={`${testIdPrefix}-detail-content`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-heading text-xl sm:text-2xl text-ink-900 truncate" data-testid={`${testIdPrefix}-detail-title`}>
            {activeMuscle.label}
          </h3>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {isPrimary && (
              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-terracotta-100 text-terracotta-700" data-testid={`${testIdPrefix}-badge-primary`}>
                Primary contributor
              </span>
            )}
            {isRecurring && (
              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1" data-testid={`${testIdPrefix}-badge-recurring`}>
                <Flame className="w-3 h-3" /> Recurring
              </span>
            )}
          </div>
        </div>
        <button type="button" onClick={onClose} data-testid={`${testIdPrefix}-detail-close`} className="p-2 -mr-2 -mt-1 rounded-full hover:bg-paper-100 text-ink-500">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Add to / remove from today's work */}
      {(onAddToWorked || onRemoveFromWorked) && (
        <div data-testid={`${testIdPrefix}-detail-actions`}>
          {isWorkedToday ? (
            <button
              type="button"
              onClick={() => onRemoveFromWorked && onRemoveFromWorked(activeMuscle.id)}
              data-testid={`${testIdPrefix}-remove-from-worked`}
              className="w-full min-h-[44px] px-4 rounded-full bg-paper-100 hover:bg-paper-200 text-ink-900 text-sm font-medium inline-flex items-center justify-center gap-2"
            >
              <span className="w-2 h-2 rounded-full bg-sage-500" />
              Treated today — tap to remove
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onAddToWorked && onAddToWorked(activeMuscle.id)}
              data-testid={`${testIdPrefix}-add-to-worked`}
              disabled={!onAddToWorked}
              className="w-full min-h-[44px] px-4 rounded-full bg-ink-900 hover:bg-ink-800 text-white text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <ChevronRight className="w-4 h-4" />
              Add to today&rsquo;s work
            </button>
          )}
        </div>
      )}

      {/* Why it matters */}
      {referral && (
        <section data-testid={`${testIdPrefix}-section-why`}>
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-terracotta-500" />
            <span className="font-medium text-ink-900 text-sm">Why it matters</span>
          </div>
          <p className="text-sm text-ink-700 leading-relaxed pl-6">{referral}</p>
        </section>
      )}

      {/* Common Symptoms */}
      {symptoms.length > 0 && (
        <section data-testid={`${testIdPrefix}-section-symptoms`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2 h-2 rounded-full bg-terracotta-500" />
            <span className="font-medium text-ink-900 text-sm">Common symptoms</span>
          </div>
          <ul className="pl-4 space-y-1">
            {symptoms.slice(0, 6).map((s, i) => (
              <li key={i} className="text-sm text-ink-700 list-disc list-inside leading-snug" data-testid={`${testIdPrefix}-symptom-${i}`}>{s}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Works With */}
      {worksWith.length > 0 && (
        <section data-testid={`${testIdPrefix}-section-works-with`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-4 h-4 inline-flex items-center justify-center text-sage-600">⚯</span>
            <span className="font-medium text-ink-900 text-sm">Works with (commonly involved)</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {worksWith.map((mid) => {
              const m = MUSCLE_BY_ID[mid] || MUSCLE_BY_ID[canonicalMuscleId(mid)];
              if (!m) return null;
              return (
                <button
                  key={mid}
                  type="button"
                  onClick={() => onMuscleSelect(mid)}
                  data-testid={`${testIdPrefix}-works-${mid}`}
                  className="px-3 py-1.5 rounded-full bg-paper-100 hover:bg-paper-200 text-xs text-ink-900"
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Evaluate Next */}
      {evaluateNext.length > 0 && (
        <section data-testid={`${testIdPrefix}-section-evaluate-next`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-4 h-4 inline-flex items-center justify-center text-amber-600">⌖</span>
            <span className="font-medium text-ink-900 text-sm">Evaluate next</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {evaluateNext.map((mid) => {
              const m = MUSCLE_BY_ID[mid] || MUSCLE_BY_ID[canonicalMuscleId(mid)];
              if (!m) return null;
              return (
                <button
                  key={mid}
                  type="button"
                  onClick={() => onMuscleSelect(mid)}
                  data-testid={`${testIdPrefix}-evaluate-${mid}`}
                  className="px-3 py-1.5 rounded-full bg-white border border-amber-200 hover:border-amber-400 text-xs text-ink-900"
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Client History */}
      {activeRow && (activeRow.count || 0) > 0 && (
        <section data-testid={`${testIdPrefix}-section-history`}>
          <div className="flex items-center gap-2 mb-2">
            {painDelta != null && painDelta > 0 ? <TrendingDown className="w-4 h-4 text-sage-600" /> : <TrendingUp className="w-4 h-4 text-terracotta-500" />}
            <span className="font-medium text-ink-900 text-sm">Client history</span>
          </div>
          <div className="grid grid-cols-3 gap-2 bg-paper-50 rounded-2xl p-3">
            <div>
              <div className="text-[10px] text-ink-500 uppercase tracking-wide">Worked</div>
              <div className="text-lg font-medium text-ink-900" data-testid={`${testIdPrefix}-history-count`}>{activeRow.count}×</div>
            </div>
            <div>
              <div className="text-[10px] text-ink-500 uppercase tracking-wide">First worked</div>
              <div className="text-xs text-ink-900 font-medium" data-testid={`${testIdPrefix}-history-first`}>
                {activeRow.first_worked ? new Date(activeRow.first_worked).toLocaleDateString() : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] text-ink-500 uppercase tracking-wide">Trend</div>
              <div className={`text-xs font-medium inline-flex items-center gap-1 ${painDelta != null && painDelta > 0 ? "text-sage-600" : "text-ink-700"}`} data-testid={`${testIdPrefix}-history-trend`}>
                {painDelta != null
                  ? (painDelta > 0 ? <><TrendingDown className="w-3 h-3" /> Improving</> : <><TrendingUp className="w-3 h-3" /> Watching</>)
                  : "—"}
              </div>
            </div>
          </div>
          {(painDelta != null || romGain != null) && (
            <div className="mt-2 flex items-center justify-between text-xs text-ink-700 px-1">
              {painDelta != null && <span data-testid={`${testIdPrefix}-history-pain`}>Pain avg drop: <strong className="text-ink-900">−{painDelta}</strong></span>}
              {romGain != null && <span data-testid={`${testIdPrefix}-history-rom`}>ROM avg gain: <strong className="text-ink-900">+{romGain}%</strong></span>}
            </div>
          )}
        </section>
      )}
      {isMobile && <div className="h-4" />}
    </div>
  );
}

// ─── CLINICAL MARKER ─────────────────────────────────────────────────────────
// High-contrast clinical marker: white outer ring + intensity-colored core,
// soft drop-shadow, optional intensity number, selected glow ring. Reads cleanly
// over muscle / bone / landmark layers and works for both heatmap & trigger
// point use cases.
function ClinicalMarker({
  cx,
  cy,
  score,                // 1–7 intensity
  selected = false,
  recurring = false,
  showNumber = true,
  filterPrefix,
  testId,
}) {
  const safeScore = Math.max(1, Math.min(7, Math.round(score || 1)));
  const fill = intensityScoreColor(safeScore);
  // Severe (red) needs slightly lighter text for legibility; others use white.
  const textColor = "#FFFFFF";

  return (
    <g
      data-testid={testId}
      data-intensity={safeScore}
      filter={`url(#${filterPrefix}-marker-shadow)`}
    >
      {/* Selected outer pulse ring — only on the active marker */}
      {selected && (
        <>
          <circle cx={cx} cy={cy} r={5.6} fill="none" stroke={fill} strokeWidth={0.45} opacity={0.55} />
          <circle cx={cx} cy={cy} r={4.4} fill="none" stroke="#FFFFFF" strokeWidth={0.55} opacity={0.9} />
        </>
      )}
      {/* Recurring dashed indicator — sits just outside the white ring */}
      {recurring && (
        <circle
          cx={cx}
          cy={cy}
          r={4.2}
          fill="none"
          stroke="#C53D2E"
          strokeWidth={0.4}
          strokeDasharray="0.9 0.7"
          opacity={0.9}
        />
      )}
      {/* White outer ring */}
      <circle cx={cx} cy={cy} r={3.3} fill="#FFFFFF" opacity={0.97} />
      {/* Intensity-colored core */}
      <circle cx={cx} cy={cy} r={2.55} fill={fill} opacity={1} />
      {/* Optional intensity number */}
      {showNumber && (
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="2.6"
          fontWeight="700"
          fill={textColor}
          style={{ pointerEvents: "none", userSelect: "none", fontFamily: "system-ui, sans-serif" }}
        >
          {safeScore}
        </text>
      )}
    </g>
  );
}

